// Protokoll (Audit-Log-Viewer, 16.07.): macht die unveränderliche Nachweiskette der EIGENEN Werkstatt
// sichtbar — Einstellungen → Protokoll, Recht audit:lesen (Floor 90). Monatsweise Navigation (MonthNav),
// serverseitiger Aktions-Filter, CSV-Export mit Formel-Injection-Guard. Der Server scopet hart auf die
// Werkstatt — diese Seite ist nur Komfort/Anzeige.
//
// Guard-Wrapper-Muster (Review-Fund 16.07.): die Rechte-Prüfung sitzt VOR der Daten-Komponente, damit ein
// Nutzer ohne Recht keinen (serverseitig ohnehin 403enden) Request auslöst, bevor <Navigate> greift.
import { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { canReadAudit, localDayKey } from '@wf/shared'
import { useAuth } from '../app/auth'
import { downloadTextFile } from '../export/csv'
import { monthRange } from '../zeiten/timeMath'
import { MonthNav } from '../zeiten/MonthNav'
import { ACTION_LABELS, actionLabel } from './actionLabels'
import { auditCsv } from './auditCsv'
import { AuditQueryState } from './AuditQueryState'
import { AuditTable } from './AuditTable'
import { useAudit } from './useAudit'

export function ProtokollPage() {
  const { user } = useAuth()
  if (!user) return null // Session lädt noch — nichts anfragen, nichts flackern
  if (!canReadAudit(user)) return <Navigate to="/overview" replace />
  return <ProtokollContent />
}

function ProtokollContent() {
  const [month, setMonth] = useState(() => localDayKey(Date.now()).slice(0, 7))
  const [action, setAction] = useState('')
  const { from, to } = monthRange(month)
  const audit = useAudit(from, to, action || undefined)

  // Aktions-Optionen: die STABILE Gesamtliste (ACTION_LABELS) ∪ unbekannte Aktionen aus den geladenen
  // Daten (neue Backend-Aktionen bleiben filterbar) — NICHT nur der gefilterte Response, sonst kollabiert
  // das Dropdown nach der ersten Auswahl auf einen Eintrag (Review-Fund 16.07., 3× unabhängig gefunden).
  const actionOptions = useMemo(() => {
    const set = new Set<string>(Object.keys(ACTION_LABELS))
    for (const e of audit.data?.entries ?? []) set.add(e.action)
    if (action) set.add(action)
    return [...set].sort((a, b) => actionLabel(a).localeCompare(actionLabel(b), 'de'))
  }, [audit.data, action])

  const entries = audit.data?.entries ?? []

  function exportCsv() {
    if (!audit.data) return
    downloadTextFile(`protokoll-${month}${action ? `-${action}` : ''}.csv`, auditCsv(audit.data.entries))
  }

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">Protokoll</h2>
          <p className="sub">Unveränderliche Nachweiskette der Werkstatt — wer hat wann was gelöscht, storniert, exportiert.</p>
        </div>
      </div>

      <MonthNav month={month} onChange={setMonth}>
        <select className="wf-input" aria-label="Aktion filtern" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">Alle Aktionen</option>
          {actionOptions.map((a) => (
            <option key={a} value={a}>
              {actionLabel(a)}
            </option>
          ))}
        </select>
        <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={exportCsv} disabled={!audit.data || entries.length === 0}>
          CSV-Export
        </button>
      </MonthNav>

      <AuditQueryState isLoading={audit.isLoading} isError={audit.isError} truncated={audit.data?.truncated ?? false} narrowHint="Zeitraum oder Aktions-Filter" />
      {audit.data ? <AuditTable entries={entries} /> : null}

      <p className="hint">
        Einträge sind <b>unveränderbar</b> (revisionssicher, DSGVO Art. 5 Abs. 2). Nach dem Löschen oder Anonymisieren eines
        Mitarbeiters erscheint statt des Namens nur noch die interne Nummer — das ist gewollter Datenschutz, keine Lücke.
      </p>
    </section>
  )
}
